/*
 * Iter XX.44.A — Founder Ops panel: feature flags + kill switches + payouts queue.
 *
 * Sits inside WebFounderDashboard. Everything gated behind admin JWT.
 */
import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

export function FounderOpsSection() {
  const [flags, setFlags] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [fRes, pRes] = await Promise.all([
        api.get("/admin/feature-flags"),
        api.get("/admin/payouts", { params: { status: "requested" } }),
      ]);
      setFlags(fRes.data);
      setPayouts(pRes.data.payouts || []);
    } catch (e) { /* ok on 403 for non-admin */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function toggleFlag(flag, next) {
    try {
      await api.post(`/admin/feature-flags/${flag}`, { enabled: next });
      toast.success(`${flag} → ${next ? "on" : "off"}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "toggle_failed"); }
  }

  async function markProcessing(id) {
    try { await api.post(`/admin/payouts/${id}/processing`); toast.success("→ processing"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "err"); }
  }

  async function markPaid(id) {
    const ref = window.prompt("Payment reference (UPI/bank txn id):");
    if (!ref) return;
    try {
      await api.post(`/admin/payouts/${id}/paid`, { payment_reference: ref });
      toast.success("Marked paid");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "err"); }
  }

  async function markFailed(id) {
    const reason = window.prompt("Reason for failure?") || "unknown";
    try {
      await api.post(`/admin/payouts/${id}/failed`, { reason });
      toast.success("Marked failed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "err"); }
  }

  async function recomputeRollups() {
    setBusy(true);
    try { const { data } = await api.post("/admin/rollups/recompute-today"); toast.success(`Refreshed ${data.refreshed} partners`); }
    catch (e) { toast.error(e?.response?.data?.detail || "err"); }
    finally { setBusy(false); }
  }

  return (
    <section data-testid="section-founder-ops" className="space-y-4">
      <p className="label-eyebrow">ops · Iter XX.44.A</p>

      {/* Kill switches */}
      {flags && (
        <div className="surface-1 p-4 mb-3">
          <div className="text-sm text-neutral-300 mb-3">Kill switches (env-controlled)</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
            {Object.entries(flags.kill_switches).map(([k, v]) => (
              <div key={k} className={`p-2 rounded border ${v ? "border-red-500/40 bg-red-500/10 text-red-400" : "border-neutral-800 text-neutral-400"}`} data-testid={`kill-${k}`}>
                <div className="font-medium">{k}</div>
                <div>{v ? "KILLED" : "ok"}</div>
              </div>
            ))}
          </div>
          <div className="text-xs text-neutral-500 mt-2">Kill switches are set via env vars (GULLY_KILL_*). No UI toggle to prevent accidents.</div>
        </div>
      )}

      {/* Feature flags */}
      {flags && (
        <div className="surface-1 p-4 mb-3">
          <div className="text-sm text-neutral-300 mb-3">Feature flags (DB-controlled)</div>
          <div className="space-y-2">
            {Object.entries(flags.flags).map(([k, v]) => (
              <div key={k} className="flex justify-between items-center text-sm py-2 border-b border-neutral-800 last:border-0" data-testid={`flag-row-${k}`}>
                <div>
                  <div className="text-neutral-200">{k}</div>
                  <div className="text-xs text-neutral-500">default: {String(flags.defaults?.[k] ?? false)}</div>
                </div>
                <button onClick={() => toggleFlag(k, !v)}
                        className={`text-xs px-3 py-1 rounded ${v ? "bg-emerald-500/20 text-emerald-300" : "bg-neutral-800 text-neutral-400"}`}
                        data-testid={`flag-toggle-${k}`}>
                  {v ? "ON" : "OFF"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending payouts */}
      <div className="surface-1 p-4">
        <div className="flex justify-between items-center mb-3">
          <div className="text-sm text-neutral-300">Pending cash-out requests ({payouts.length})</div>
          <button onClick={recomputeRollups} disabled={busy}
                  className="text-xs text-neutral-500 hover:text-emerald-300"
                  data-testid="ops-recompute-rollups">
            {busy ? "…" : "Recompute rollups"}
          </button>
        </div>
        {payouts.length === 0 ? (
          <div className="text-sm text-neutral-500">No pending requests.</div>
        ) : (
          <table className="w-full text-sm" data-testid="ops-payouts-table">
            <thead className="text-xs text-neutral-500">
              <tr className="border-b border-neutral-800">
                <th className="text-left py-2">Amount</th>
                <th className="text-left">Method</th>
                <th className="text-left">Partner</th>
                <th className="text-left">Requested</th>
                <th className="text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map(p => (
                <tr key={p.payout_id} className="border-b border-neutral-900" data-testid={`payout-row-${p.payout_id}`}>
                  <td className="py-2 text-emerald-300">₹{(p.amount_minor / 100).toLocaleString()}</td>
                  <td className="uppercase text-xs">{p.method}{p.country ? ` · ${p.country}` : ""}</td>
                  <td className="text-xs text-neutral-400">{p.partner_id}</td>
                  <td className="text-xs text-neutral-500">{new Date(p.requested_at).toLocaleString()}</td>
                  <td className="text-xs">
                    <div className="flex gap-1">
                      <button onClick={() => markProcessing(p.payout_id)}
                              className="px-2 py-0.5 rounded border border-neutral-800 hover:border-blue-500 text-neutral-400 hover:text-blue-300"
                              data-testid={`payout-processing-${p.payout_id}`}>Processing</button>
                      <button onClick={() => markPaid(p.payout_id)}
                              className="px-2 py-0.5 rounded border border-neutral-800 hover:border-emerald-500 text-neutral-400 hover:text-emerald-300"
                              data-testid={`payout-paid-${p.payout_id}`}>Mark paid</button>
                      <button onClick={() => markFailed(p.payout_id)}
                              className="px-2 py-0.5 rounded border border-neutral-800 hover:border-red-500 text-neutral-400 hover:text-red-300"
                              data-testid={`payout-failed-${p.payout_id}`}>Fail</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
